<template>
    <main class="flex justify-center bg-gray-50">
        <HeaderComponent />
        <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">
            <!-- Notifications -->
            <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
                <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                    {{ errorMessage }}
                </div>
                <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                    {{ successMessage }}
                </div>
            </div>

            <!-- Main content -->
            <div class="max-w-6xl mx-auto w-full">
                <!-- Tabs navigation -->
                <div class="flex border-b parent border-gray-200 mb-6">
                    <button 
                        @click="toggleSales()" 
                        :class="{'border-b-2 border-orange-500 text-orange-500': isSales, 'text-gray-500 hover:text-gray-700': !isSales}"
                        class="py-4 px-6 text-sm font-medium flex items-center"
                    >
                        <i class="fas fa-shopping-cart mr-2"></i>
                        <span class="md:flex hidden">Ventes en ligne </span> ({{ sales.length }})
                        <span v-if="unvalidatedSales > 0" class="ml-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            {{ unvalidatedSales }} non validé(s)
                        </span>
                    </button>
                    <button 
                        @click="toggleNewsletter()" 
                        :class="{'border-b-2 border-orange-500 text-orange-500': isNewsletter, 'text-gray-500 hover:text-gray-700': !isNewsletter}"
                        class="py-4 px-6 text-sm font-medium flex items-center"
                    >
                        <i class="fas fa-envelope mr-2"></i>
                        <span class="md:flex hidden">Newsletter </span> ({{ newsletter.length }})
                    </button>
                    <button 
                        @click="toggleMessage()" 
                        :class="{'border-b-2 border-orange-500 text-orange-500': isMessage, 'text-gray-500 hover:text-gray-700': !isMessage}"
                        class="py-4 px-6 text-sm font-medium flex items-center"
                    >
                        <i class="fas fa-comments mr-2"></i>
                        <span class="md:flex hidden">Messages </span> ({{ messages.length }})
                    </button>
                </div>

                <!-- Sales section -->
                <div v-if="isSales" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div class="p-4 border-b border-gray-200 flex justify-between items-center">
                        <h2 class="text-lg font-semibold text-gray-800">Ventes en ligne</h2>
                        
                    </div>
                    
                    <div class="divide-y divide-gray-200">
                        <div 
                            v-for="sale in sales" 
                            :key="sale._id" 
                            :class="{'bg-orange-50': !sale.isValide}"
                            class="p-4 hover:bg-gray-50 cursor-pointer"
                            @click="openSale(sale)"
                        >
                            <div class="flex justify-between items-start">
                                <div>
                                    <div class="flex items-center">
                                        <h3 class="text-sm font-medium text-gray-900">
                                            Commande #{{ sale._id.toString().substring(18, 24) }}
                                        </h3>
                                        <span v-if="!sale.isValide" class="ml-2 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">À valider</span>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-1">{{ sale.client.nom }} {{ sale.client.prenoms }}</p>
                                    <p class="text-xs text-gray-500">{{ sale.client.whatsapp }} · {{ sale.client.email }}</p>
                                </div>
                                <div class="text-right">
                                    <p class="text-sm font-bold text-orange-600">{{ formatPrice(sale.total) }}</p>
                                    <p class="text-xs text-gray-500">{{ formatDate(sale.createdAt) }}</p>
                                </div>
                            </div>
                            <div class="mt-2 flex flex-wrap gap-1">
                                <span 
                                    v-for="(product, index) in sale.produits.slice(0, 3)" 
                                    :key="index"
                                    class="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded"
                                >
                                    {{ product.quantity }}x {{ product.lotName }}
                                </span>
                                <span v-if="sale.produits.length > 3" class="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                    +{{ sale.produits.length - 3 }} autres
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <div v-if="sales.length === 0" class="p-8 text-center">
                        <i class="fas fa-shopping-cart text-4xl text-gray-300 mb-3"></i>
                        <p class="text-gray-500">Aucune vente en ligne enregistrée</p>
                    </div>
                </div>

                <!-- Newsletter section -->
                <div v-if="isNewsletter" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div class="p-4 border-b border-gray-200 flex justify-between items-center">
                        <h2 class="text-lg font-semibold text-gray-800">Abonnés à la newsletter</h2>
                    </div>
                    
                    <div class="divide-y divide-gray-200">
                        <div 
                            v-for="(email, index) in newsletter" 
                            :key="email._id" 
                            class="p-4 hover:bg-gray-50 flex items-center"
                        >
                            <div class="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mr-4 flex-shrink-0">
                                {{ index + 1 }}
                            </div>
                            <div class="flex-grow">
                                <div class="flex justify-between items-center">
                                    <p class="text-sm font-medium text-gray-900">{{ email.email }}</p>
                                    <span class="text-xs text-gray-500">{{ formatDate(email.date) }}</span>
                                </div>
                                <p class="text-xs text-gray-500 mt-1">Inscrit le {{ formatFullDate(email.date) }}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div v-if="newsletter.length === 0" class="p-8 text-center">
                        <i class="fas fa-envelope-open-text text-4xl text-gray-300 mb-3"></i>
                        <p class="text-gray-500">Aucun abonné à la newsletter</p>
                    </div>
                </div>

                <!-- Messages section -->
                <div v-if="isMessage" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div class="p-4 border-b border-gray-200">
                        <h2 class="text-lg font-semibold text-gray-800">Messages reçus</h2>
                    </div>
                    
                    <div class="divide-y divide-gray-200">
                        <div 
                            v-for="message in messages" 
                            :key="message._id"
                            class="p-4 hover:bg-gray-50 cursor-pointer"
                        >
                            <div class="flex items-start">
                                <div class="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-4 flex-shrink-0">
                                    <i class="fas fa-user"></i>
                                </div>
                                <div class="flex-grow">
                                    <div class="flex justify-between items-center">
                                        <h3 class="text-sm font-medium text-gray-900">
                                            {{ message.prenoms }} {{ message.nom }}
                                        </h3>
                                        <span class="text-xs text-gray-500">{{ formatDate(message.date) }}</span>
                                    </div>
                                    <p class="text-xs text-gray-500">{{ message.email }} · {{ message.numero }}</p>
                                    <p class="text-sm text-gray-700 mt-2">{{ message.content }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div v-if="messages.length === 0" class="p-8 text-center">
                        <i class="fas fa-comment-slash text-4xl text-gray-300 mb-3"></i>
                        <p class="text-gray-500">Aucun message reçu</p>
                    </div>
                </div>
            </div>

            <div v-if="selectedSale" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style="left: 280px;">
                <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
                    <div class="p-6">
                        <div class="flex justify-between items-center mb-4">
                            <h2 class="text-xl font-bold text-gray-800">
                                Commande #{{ selectedSale._id.toString().substring(18, 24) }}
                                <span v-if="!selectedSale.isValide" class="ml-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">À valider</span>
                            </h2>
                            <button @click="selectedSale = null" class="text-gray-400 hover:text-gray-600">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div class="mb-6">
                            <div class="bg-gray-50 p-4 rounded-lg mb-4">
                                <h3 class="text-sm font-semibold text-gray-800 mb-2">Informations client</h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p class="text-sm text-gray-600"><span class="font-medium">Nom:</span> {{ selectedSale.client.nom }} {{ selectedSale.client.prenoms }}</p>
                                        <p class="text-sm text-gray-600"><span class="font-medium">WhatsApp:</span> {{ selectedSale.client.whatsapp }}</p>
                                    </div>
                                    <div>
                                        <p class="text-sm text-gray-600"><span class="font-medium">Email:</span> {{ selectedSale.client.email || 'Non renseigné' }}</p>
                                        <p class="text-sm text-gray-600"><span class="font-medium">Adresse:</span> {{ selectedSale.client.adresse }}</p>
                                    </div>
                                </div>
                                <a 
                                    :href="'https://wa.me/' + selectedSale.client.whatsapp.replace(/[^\d+]/g, '')" 
                                    target="_blank"
                                    class="inline-block mt-3 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium"
                                >
                                    <i class="fab fa-whatsapp mr-2"></i> Contacter
                                </a>
                            </div>
                            
                            <h3 class="text-sm font-semibold text-gray-800 mb-2">Détails de la commande</h3>
                            <div class="border border-gray-200 rounded-lg overflow-hidden">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead class="bg-gray-50">
                                        <tr>
                                            <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500">Produit</th>
                                            <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500">Qté</th>
                                            <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500">Prix unit.</th>
                                            <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody class="bg-white divide-y divide-gray-200">
                                        <tr v-for="(product, index) in selectedSale.produits" :key="index">
                                            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {{ product.lotName }}
                                                <div v-if="product.capacities.length" class="text-xs text-gray-500">
                                                    {{ product.capacities[0] }}
                                                </div>
                                                <div v-if="product.colors.length" class="text-xs text-gray-500">
                                                    {{ product.colors[0] }}
                                                </div>
                                            </td>
                                            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                {{ product.quantity }}
                                            </td>
                                            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                {{ formatPrice(product.prix_reduit || product.prix) }}
                                            </td>
                                            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                {{ formatPrice(product.stotal) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot class="bg-gray-50">
                                        <tr>
                                            <td colspan="3" class="px-4 py-3 text-right text-sm font-medium text-gray-900">Total</td>
                                            <td class="px-4 py-3 text-sm font-bold text-orange-600">
                                                {{ formatPrice(selectedSale.total) }}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            
                            <div class="mt-4 text-xs text-gray-500">
                                Commande passée le {{ formatFullDate(selectedSale.createdAt) }}
                            </div>
                        </div>
                        
                        <div class="flex justify-between">
                            <button 
                                @click="deleteSale(selectedSale._id)" 
                                class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium text-sm flex items-center"
                            >
                                <i class="fas fa-trash mr-2"></i>
                                Supprimer
                            </button>
                            <button 
                                v-if="!selectedSale.isValide"
                                @click="validateSale(selectedSale._id)" 
                                class="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm flex items-center"
                            >
                                <i class="fas fa-check mr-2"></i>
                                Valider la commande
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Message detail modal -->
            <div v-if="selectedMessage" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style="left: 280px;">
                <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
                    <div class="p-6">
                        <div class="flex justify-between items-center mb-4">
                            <h2 class="text-xl font-bold text-gray-800">Détails du message</h2>
                            <button @click="selectedMessage = null" class="text-gray-400 hover:text-gray-600">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div class="mb-6">
                            <div class="flex items-center mb-4">
                                <div class="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-4">
                                    <i class="fas fa-user text-xl"></i>
                                </div>
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900">{{ selectedMessage.prenoms }} {{ selectedMessage.nom }}</h3>
                                    <p class="text-sm text-gray-500">{{ selectedMessage.email }} · {{ selectedMessage.numero }}</p>
                                </div>
                            </div>
                            
                            <div class="bg-gray-50 p-4 rounded-lg">
                                <p class="text-gray-700 whitespace-pre-line">{{ selectedMessage.content }}</p>
                            </div>
                            
                            <div class="mt-4 text-xs text-gray-500">
                                Reçu le {{ formatFullDate(selectedMessage.date) }}
                            </div>
                        </div>
                        
                        <div class="flex justify-end">
                            <button 
                                @click="deleteMessage(selectedMessage._id)" 
                                class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium text-sm flex items-center"
                            >
                                <i class="fas fa-trash mr-2"></i>
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup>
import HeaderComponent from "../components/HeaderComponent.vue";
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';

const router = useRouter();
const authStore = useAuthStore();
const user = computed(() => authStore.user);

// Data
const sales = ref([]);
const messages = ref([]);
const newsletter = ref([]);
const errorMessage = ref('');
const successMessage = ref('');
const isSales = ref(true); // Actif par défaut
const isMessage = ref(false);
const isNewsletter = ref(false);
const selectedMessage = ref(null);
const selectedSale = ref(null);

// Computed
const unreadMessages = computed(() => {
    return messages.value.filter(m => !m.read).length;
});

const unvalidatedSales = computed(() => {
    return sales.value.filter(s => !s.isValide).length;
});

// Methods
function toggleSales() {
    isSales.value = true;
    isMessage.value = false;
    isNewsletter.value = false;
}

function toggleNewsletter() {
    isSales.value = false;
    isMessage.value = false;
    isNewsletter.value = true;
}

function toggleMessage() {
    isSales.value = false;
    isMessage.value = true;
    isNewsletter.value = false;
}

function formatPrice(price) {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
}

function formatDate(date) {
    return new Date(date).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
    });
}

function formatFullDate(date) {
    return new Date(date).toLocaleDateString('fr-FR', { 
        weekday: 'long',
        day: '2-digit', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

async function openSale(sale) {
    selectedSale.value = sale;
}

async function fetchSales() {
    try {
        const response = await axios.get('/api/online');
        sales.value = response.data;
    } catch (error) {
        errorMessage.value = 'Erreur lors de la récupération des ventes';
        hideErrorMessageAfterDelay();
    }
}

async function validateSale(id) {
    try {
        await axios.put(`/api/online/${id}/validate`);
        const sale = sales.value.find(s => s._id === id);
        if (sale) sale.isValide = true;
        selectedSale.value = null;
        successMessage.value = 'Commande validée avec succès';
        hideSuccessMessageAfterDelay();
    } catch (error) {
        errorMessage.value = 'Erreur lors de la validation de la commande';
        hideErrorMessageAfterDelay();
    }
}

async function deleteSale(id) {
    if (confirm('Voulez-vous vraiment supprimer cette commande ?')) {
        try {
            await axios.delete(`/api/online/${id}`);
            sales.value = sales.value.filter(s => s._id !== id);
            selectedSale.value = null;
            successMessage.value = 'Commande supprimée avec succès';
            hideSuccessMessageAfterDelay();
        } catch (error) {
            errorMessage.value = 'Erreur lors de la suppression de la commande';
            hideErrorMessageAfterDelay();
        }
    }
}

async function exportSales() {
    try {
        const response = await axios.get('/api/online/export', {
            responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'ventes_en_ligne.csv');
        document.body.appendChild(link);
        link.click();
        
        successMessage.value = 'Export réalisé avec succès';
        hideSuccessMessageAfterDelay();
    } catch (error) {
        errorMessage.value = 'Erreur lors de l\'export des ventes';
        hideErrorMessageAfterDelay();
    }
}


async function openMessage(message) {
    try {
        // Mark as read when opening
        if (!message.read) {
            await axios.put(`/api/messages/${message._id}/read`);
            message.read = true;
        }
        selectedMessage.value = message;
    } catch (error) {
        errorMessage.value = "Erreur lors de l'ouverture du message";
        hideErrorMessageAfterDelay();
    }
}

async function fetchNewsletter() {
    try {
        const response = await axios.get('/api/newsletter/subscribers');
        newsletter.value = response.data;
    } catch (error) {
        errorMessage.value = 'Erreur lors de la récupération des emails';
        hideErrorMessageAfterDelay();
    }
}

async function fetchMessages() {
    try {
        const response = await axios.get('/api/messages/getAll');
        messages.value = response.data;
    } catch (error) {
        errorMessage.value = 'Erreur lors de la récupération des messages';
        hideErrorMessageAfterDelay();
    }
}

async function exportNewsletter() {
    try {
        const response = await axios.get('/api/newsletter/export', {
            responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'liste_abonnes_newsletter.csv');
        document.body.appendChild(link);
        link.click();
        
        successMessage.value = 'Export réalisé avec succès';
        hideSuccessMessageAfterDelay();
    } catch (error) {
        errorMessage.value = 'Erreur lors de l\'export des emails';
        hideErrorMessageAfterDelay();
    }
}

async function deleteMessage(id) {
    try {
        await axios.delete(`/api/messages/${id}`);
        messages.value = messages.value.filter(m => m._id !== id);
        selectedMessage.value = null;
        successMessage.value = 'Message supprimé avec succès';
        hideSuccessMessageAfterDelay();
    } catch (error) {
        errorMessage.value = 'Erreur lors de la suppression du message';
        hideErrorMessageAfterDelay();
    }
}

async function confirmDeleteEmail(id) {
    if (confirm('Voulez-vous vraiment supprimer cet abonné de la newsletter ?')) {
        try {
            await axios.delete(`/api/newsletter/${id}`);
            newsletter.value = newsletter.value.filter(e => e._id !== id);
            successMessage.value = 'Email supprimé avec succès';
            hideSuccessMessageAfterDelay();
        } catch (error) {
            errorMessage.value = 'Erreur lors de la suppression de l\'email';
            hideErrorMessageAfterDelay();
        }
    }
}

function hideSuccessMessageAfterDelay() {
    setTimeout(() => {
        successMessage.value = ''; 
    }, 5000);
}

function hideErrorMessageAfterDelay() {
    setTimeout(() => {
        errorMessage.value = ''; 
    }, 10000);
}

// Lifecycle hooks
onMounted(() => {
    fetchSales();
    fetchNewsletter();
    fetchMessages();
    if (!user.value.isSuper) {
        router.push("/dashboard");
    }
});
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border-left-color: rgb(234, 88, 12);
    animation: spin 1s ease infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>